import * as Sentry from '@sentry/vue'
import { ToolsClass } from '@/base/class/tools.class'

/**
 * Jedyne wejście do raportowania błędów (Sentry).
 *
 * Zamiast wołać `Sentry.*` bezpośrednio w kodzie, wołamy `reportError` / `reportMessage`. Dzięki temu
 * mamy jedno miejsce na: bramkę środowiskową, czyszczenie danych wrażliwych i wspólne tagi.
 *
 * Czego NIE robi: nie inicjalizuje SDK (to `initSentry` w `utils/sentryInit.js`) i nie decyduje,
 * co jest błędem - to zadanie miejsca wywołania.
 */

export const REPORT_LEVEL = {
  fatal: 'fatal',
  error: 'error',
  warning: 'warning',
  info: 'info',
}

const REDACTED = '[usunięte]'

/** Limity chronią przed wysłaniem całych odpowiedzi API jako `extra`. */
const MAX_DEPTH = 4
const MAX_STRING_LENGTH = 500
const MAX_ARRAY_LENGTH = 20
const MAX_KEYS = 50

/**
 * Klucze, których wartości nie opuszczają przeglądarki. Dane karty i dane osobowe płatnika -
 * ich obecność w Sentry byłaby naruszeniem, a do diagnozy nie są potrzebne.
 *
 * Świadomie NIE ma tu `apiToken` / `hash` / `orderId`: to klucze korelacji z logami backendu,
 * a i tak trafiają do Sentry w URL-u zdarzenia (są segmentami ścieżki).
 */
const SENSITIVE_KEY_PATTERN =
  /(card|pan|cvv|cvc|blik|e-?mail|phone|msisdn|password|passwd|secret|authorization|pesel|nip|iban|nrb|account)/i

/**
 * Klucze korelacji z logami backendu. Ich wartości bywają 13-19-cyfrowe, więc maskowanie numerów kart
 * (patrz PAN_PATTERN) potrafiłoby je zjeść - suma Luhna wypada dodatnio dla co dziesiątej takiej liczby.
 * Tu pomijamy maskowanie i zostawiamy samo przycięcie długości.
 *
 * Prefiks `params.`/`query.` jest opcjonalny, bo przez ten sam sanitizer idą atrybuty spanów
 * (`beforeSendTransaction` w `utils/sentryInit.js`), a instrumentacja routera z `@sentry/vue`
 * przepisuje parametry trasy właśnie pod takimi nazwami. Bez prefiksu `params.orderId` trafiałby
 * na maskowanie numerów kart - 14-cyfrowy `orderId` z dodatnią sumą Luhna znikałby jako „[usunięte]".
 */
const CORRELATION_KEY_PATTERN =
  /^(params\.|query\.)?(apitoken|token|hash|orderid|idtransremote|transremoteid|serviceid|sessionid)$/i

/**
 * Kandydaci na numer karty w treści błędu: 13-19 cyfr z opcjonalnymi separatorami.
 * Sam wzorzec nie wystarcza - orderId bywa 14-cyfrowy - więc trafienia weryfikuje jeszcze suma Luhna.
 */
const PAN_PATTERN = /\b\d(?:[ -]?\d){12,18}\b/g

const EMAIL_PATTERN = /[\w.+-]+@[\w-]+\.[\w.-]+/g

/**
 * Metoda sprawdzająca, czy SDK zostało zainicjalizowane (poza produkcją `Sentry.init` nie jest wołane).
 * @returns {boolean}
 */
function isReportingEnabled() {
  try {
    return !!Sentry.getClient()
  } catch {
    return false
  }
}

/**
 * Metoda sprawdzająca sumę kontrolną Luhna - odróżnia numer karty od innego ciągu cyfr
 * (orderId, kwota w groszach, timestamp).
 * @param {string} digits
 * @returns {boolean}
 */
function isLuhnValid(digits) {
  let sum = 0
  let double = false
  for (let i = digits.length - 1; i >= 0; i--) {
    let digit = Number(digits[i])
    if (double) {
      digit *= 2
      if (digit > 9) {
        digit -= 9
      }
    }
    sum += digit
    double = !double
  }
  return sum % 10 === 0
}

/**
 * Metoda przycinająca string do limitu długości.
 * @param {string} value
 * @returns {string}
 */
function truncate(value) {
  return value.length > MAX_STRING_LENGTH ? `${value.slice(0, MAX_STRING_LENGTH)}…` : value
}

/**
 * Metoda czyszcząca pojedynczy string z danych wrażliwych i przycinająca go do limitu.
 * @param {string} value
 * @returns {string}
 */
function sanitizeString(value) {
  const masked = value
    .replace(PAN_PATTERN, (match) => (isLuhnValid(match.replace(/[ -]/g, '')) ? REDACTED : match))
    .replace(EMAIL_PATTERN, REDACTED)
  return truncate(masked)
}

/**
 * Metoda serializująca błąd do postaci czytelnej w Sentry.
 * Sentry normalizuje ErrorEvent i obiekty zewnętrznych SDK do pustego obiektu, więc pola wyciągamy ręcznie.
 * @param {Error} error
 * @returns {object}
 */
function serializeError(error) {
  return {
    name: error.name,
    message: sanitizeString(String(error.message ?? '')),
  }
}

/**
 * Metoda rekurencyjnie czyszcząca dane kontekstowe: usuwa wartości spod kluczy wrażliwych,
 * maskuje dane w stringach i ogranicza rozmiar struktury. Odporna na cykle.
 * @param {*} value
 * @param {number} [depth]
 * @param {WeakSet} [seen]
 * @returns {*}
 */
export function sanitizeReportData(value, depth = 0, seen = new WeakSet()) {
  if (value === null || value === undefined) {
    return value
  }
  if (typeof value === 'string') {
    return sanitizeString(value)
  }
  if (typeof value === 'number' || typeof value === 'boolean') {
    return value
  }
  if (typeof value === 'function' || typeof value === 'symbol') {
    return `[${typeof value}]`
  }
  if (value instanceof Error) {
    return serializeError(value)
  }
  if (depth >= MAX_DEPTH) {
    return '[zbyt zagnieżdżone]'
  }
  if (seen.has(value)) {
    return '[cykl]'
  }
  seen.add(value)

  if (Array.isArray(value)) {
    const items = value.slice(0, MAX_ARRAY_LENGTH).map((item) => sanitizeReportData(item, depth + 1, seen))
    if (value.length > MAX_ARRAY_LENGTH) {
      items.push(`[+${value.length - MAX_ARRAY_LENGTH} pominięto]`)
    }
    return items
  }

  const result = {}
  const entries = Object.entries(value).slice(0, MAX_KEYS)
  entries.forEach(([key, item]) => {
    if (SENSITIVE_KEY_PATTERN.test(key)) {
      result[key] = REDACTED
      return
    }
    if (CORRELATION_KEY_PATTERN.test(key) && typeof item === 'string') {
      result[key] = truncate(item)
      return
    }
    result[key] = sanitizeReportData(item, depth + 1, seen)
  })
  return result
}

/**
 * Metoda zamieniająca dowolną wartość na instancję Error.
 * Bez tego Sentry grupuje wszystkie zgłoszenia "obiektowe" w jeden nieczytelny worek.
 * @param {*} value
 * @param {string} fallbackMessage
 * @returns {{error: Error, original: *}}
 */
function toError(value, fallbackMessage) {
  if (value instanceof Error) {
    return { error: value, original: null }
  }
  const message =
    (typeof value === 'string' && value) || value?.message || value?.statusMessage || value?.statusText || null
  const error = new Error(sanitizeString(String(message ?? fallbackMessage)))
  error.name = 'NonError'
  return { error, original: value }
}

/**
 * Metoda budująca kontekst zgłoszenia w formacie przyjmowanym przez Sentry.
 *
 * ⚠️ `fingerprint` jest jedynym narzędziem, jakim dysponujemy do sterowania grupowaniem: Sentry skleja
 * zdarzenia po treści komunikatu, więc każde odrzucenie API o treści `Bad Request` wpadało do jednej
 * grupy - niezależnie od tego, czy padł finalny submit, czy walidacja merchanta Apple Pay. Ten projekt
 * **nie ma** `fingerprinting_rules` po stronie serwera Sentry, więc rozdzielić da się to wyłącznie
 * z kodu. Konwencja: `[flow, miejsce]`, np. `['payment-update', 'paywall-submit']` - pierwszy element
 * odpowiada tagowi `flow`, drugi rozdziela wywołania w obrębie jednego przepływu.
 * Skutek uboczny, o którym trzeba wiedzieć: nowy `fingerprint` **tworzy nową grupę**, a stara zamilka -
 * nie czytaj tego jako naprawy. → `doc/dokumentacja/sentry.md` §5
 * @param {object} context
 * @param {object} [extraDefaults]
 * @returns {object}
 */
function buildCaptureContext(context = {}, extraDefaults = {}) {
  const { extra, tags, level, fingerprint } = context
  const captureContext = {}
  const mergedExtra = { ...extraDefaults, ...(extra || {}) }

  if (Object.keys(mergedExtra).length) {
    captureContext.extra = sanitizeReportData(mergedExtra)
  }
  if (tags) {
    captureContext.tags = tags
  }
  if (level) {
    captureContext.level = level
  }
  if (fingerprint) {
    captureContext.fingerprint = fingerprint
  }
  return captureContext
}

/**
 * Metoda logująca zgłoszenie na konsolę, gdy włączony jest tryb debug.
 * @param {string} label
 * @param {*} payload
 * @param {object} captureContext
 */
function debugLog(label, payload, captureContext) {
  try {
    if (ToolsClass.isDebugEnabled()) {
      console.log(`errorReporting.${label}`, payload, captureContext)
    }
  } catch {
    // tryb debug nie może przeszkodzić w raportowaniu
  }
}

/**
 * Metoda uodparniająca funkcję raportującą: awaria raportowania nie może wywrócić wywołującego.
 * Wołamy je na ścieżce płatności - rzucający reporter przerwałby transakcję użytkownika.
 * @param {Function} fn
 * @returns {Function}
 */
function failSafe(fn) {
  return (...args) => {
    try {
      return fn(...args)
    } catch {
      return null
    }
  }
}

/**
 * Metoda zgłaszająca błąd (wyjątek, odrzucony promise, błąd zewnętrznego SDK).
 * @param {*} error dowolna wartość - nie-błędy są opakowywane w Error
 * @param {object} [context] `{ extra, tags, level, fingerprint }`
 * @param {string} [context.fallbackMessage] treść, gdy z wartości nie da się wyciągnąć komunikatu
 * @returns {string|null} id zdarzenia albo null, gdy nie raportowano
 */
export const reportError = failSafe((error, context = {}) => {
  const { error: normalizedError, original } = toError(error, context.fallbackMessage || 'Nieznany błąd')
  const captureContext = buildCaptureContext(context, original === null ? {} : { originalError: original })

  debugLog('reportError', normalizedError, captureContext)

  if (!isReportingEnabled()) {
    return null
  }
  return Sentry.captureException(normalizedError, captureContext)
})

/**
 * Metoda zgłaszająca komunikat - dla sytuacji, które nie są wyjątkiem, ale nie powinny się zdarzyć.
 * @param {string} message
 * @param {object} [context] `{ extra, tags, level, fingerprint }`, domyślny poziom to `warning`
 * @returns {string|null} id zdarzenia albo null, gdy nie raportowano
 */
export const reportMessage = failSafe((message, context = {}) => {
  const captureContext = buildCaptureContext({ level: REPORT_LEVEL.warning, ...context })

  debugLog('reportMessage', message, captureContext)

  if (!isReportingEnabled()) {
    return null
  }
  return Sentry.captureMessage(sanitizeString(String(message)), captureContext)
})

/**
 * Metoda decydująca, czy dane wystąpienie ma zostać zaraportowane - do PRÓBKOWANIA raportów, których
 * przyczyna jest już ustalona, a wolumen przytłacza projekt.
 *
 * Próbkujemy wyłącznie SAM RAPORT, nigdy detekcji incydentu: `SecureBack.vue` liczy utratę parametrów
 * przy każdym powrocie (i przy każdym ustawia `errorAlreadyReported`), a losowanie decyduje tylko
 * o wysłaniu zdarzenia. Inaczej dla 49 z 50 powrotów odżyłaby druga grupa - „ErrorTemplate: brak kodu
 * błędu [SecureBack]" (~1550 zdarzeń na dobę), którą ta flaga wycisza.
 *
 * ⚠️ Przy czytaniu Sentry pamiętaj, że licznik grupy jest wtedy ILORAZEM realnego wolumenu -
 * miejsce wołające musi podać stopę próbkowania w `extra`, inaczej pomiar da się przeczytać
 * pięćdziesięciokrotnie za nisko. Rozkłady tagów pozostają reprezentatywne.
 *
 * @param {number} sampleRate udział raportowanych wystąpień (0 = nic, 1 = wszystko)
 * @param {function} [random] źródło losowości - wstrzykiwane dla testowalności
 * @returns {boolean}
 */
export function shouldSampleReport(sampleRate, random = Math.random) {
  if (typeof sampleRate !== 'number' || Number.isNaN(sampleRate) || sampleRate <= 0) {
    return false
  }
  if (sampleRate >= 1) {
    return true
  }
  return random() < sampleRate
}

/**
 * Metoda dopisująca ślad do zgłoszeń wysłanych PÓŹNIEJ (sam w sobie nic nie wysyła).
 * Przydatne do zapisania kroków użytkownika przed błędem.
 * @param {object} breadcrumb `{ category, message, data, level }`
 */
export const addReportBreadcrumb = failSafe(({ category, message, data, level = REPORT_LEVEL.info } = {}) => {
  if (!isReportingEnabled()) {
    return
  }
  Sentry.addBreadcrumb({
    category,
    message: message ? sanitizeString(String(message)) : undefined,
    data: data ? sanitizeReportData(data) : undefined,
    level,
  })
})
